import { Copy, Paperclip, Trash2 } from "lucide-react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";
import type { AttachmentsState } from "@/modules/docs/useAttachments";
import { useDocsPeople } from "@/modules/docs/useDocsPeople";
import { relativeTime } from "@/modules/docs/versions";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * The files on a page (FR-DOC-12), as a quiet row under it — drawn only once
 * the page has some, or one is uploading, or an upload was refused (#641).
 * "Attach a file" lives in the page's "⋯" menu and a file can be dropped
 * anywhere on the page.
 */
export function AttachmentsSection({
  state,
  canWrite,
  copyLinks,
}: {
  state: AttachmentsState;
  canWrite: boolean;
  /** True in edit mode: each row also offers a link for the draft. */
  copyLinks: boolean;
}) {
  const data = useDataSource();
  const people = useDocsPeople();
  const { attachments, rejection, copiedId, requestDelete, copyLink } = state;

  if (attachments.length === 0 && !state.uploading && !rejection) return null;

  return (
    <section className="space-y-2 border-t pt-4">
      <h3 className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <Paperclip className="size-3.5" aria-hidden />
        Attachments · {String(attachments.length)}
        {state.uploading ? " · uploading…" : ""}
      </h3>

      {attachments.length === 0 ? null : (
        <ul className="space-y-1.5">
          {attachments.map((attachment) => (
            <li key={attachment.id} className="flex items-center gap-2 text-sm">
              {attachment.contentType.startsWith("image/") ? (
                // The same authenticated route a download uses — an `<img>`
                // request ignores `Content-Disposition: attachment`, so this
                // renders inline instead of prompting a save (route's own
                // comment on why one route serves both).
                <img
                  src={data.docs.attachmentUrl(attachment.id)}
                  alt=""
                  className="size-8 shrink-0 rounded border object-cover"
                />
              ) : (
                <span className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded border">
                  <Paperclip className="size-4" aria-hidden />
                </span>
              )}
              <a
                href={data.docs.attachmentUrl(attachment.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate font-medium underline-offset-2 hover:underline"
              >
                {attachment.filename}
              </a>
              <span className="text-muted-foreground hidden shrink-0 text-xs sm:inline">
                {formatBytes(attachment.sizeBytes)} ·{" "}
                {people.principalName("user", attachment.uploadedByUserId)} ·{" "}
                {relativeTime(attachment.uploadedAt)}
              </span>
              {copyLinks ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon-xs"
                      variant="ghost"
                      aria-label="Copy link"
                      onClick={() => {
                        copyLink(attachment);
                      }}
                    >
                      <Copy />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {copiedId === attachment.id ? "Copied" : "Copy link"}
                  </TooltipContent>
                </Tooltip>
              ) : null}
              {canWrite ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon-xs"
                      variant="ghost"
                      aria-label="Delete attachment"
                      onClick={() => {
                        requestDelete(attachment);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {rejection ? <RejectionNotice rejection={rejection} /> : null}
    </section>
  );
}
